import * as React from 'react';
import { Component, inject, deps, observer } from '../lib/component';
import { style } from 'typestyle';
import { percent, px } from 'csx';
import { centerCenter, horizontal, wrap } from 'csstips';
import { AddRemoveCompat } from '../components/compat/add-remove-compat';
import { PlayButtonCompat } from '../components/compat/play-button-compat';
import { FavouriteButtonCompat } from '../components/compat/favourite-button-compat';
import { Loading } from '../components/loading/loading';
import { FavouriteArtistButtonCompat } from '../components/compat/favourite-artist-button-compat';
import { BrandSponsoredLabelCompat } from '../components/compat/brand-sponsored-label-compat';
import { AccountLayout } from '../components/account/layout';
import {ReleaseTracks} from '../components/release-tracks';
import {ButtonPill} from '../components/button-pill';
import {ArtistTile} from '../components/artist-tile';
import {colorSlate, colorSubtle, colorWhite} from '../theme/color';
import {mediaMobileOnly} from '../theme/media';
import {AccountCommunicationPage} from './account-communication';

export interface AccountFavouritesPageProps {
  i18n?: {
    loading: string;
    title: string;
    titleTracks: string;
    titleArtists: string;
  }
}

@inject(deps) @observer
export class AccountFavouritesPage extends Component<AccountFavouritesPageProps> {
  render() {
    const { i18n = AccountFavouritesPage.i18n } = this.props;
    const model = this.props.model.page.favourites;
    const controller = this.props.controller.page.favourites;
    const { isMobile } = this.props.model.ui;
    const { preloadImage, preloadWaveformSvg } = this.props.controller.image;
    const {handleClickLink} = this.props.controller.ui;

    if (model.loading) return (
      <AccountLayout className={AccountFavouritesPage.styles.container}>
        <div className={AccountCommunicationPage.styles.containerLoading}>
          <Loading />
        </div>
      </AccountLayout>
    );

    return (
      <AccountLayout className={AccountFavouritesPage.styles.container}>
        {!model.hasFavourites && (
          <div className={AccountFavouritesPage.styles.empty}>
            {model.noFavourites}
          </div>
        )}
        {model.hasFavourites && (
          <div className={AccountFavouritesPage.styles.split}>
            <div className={AccountFavouritesPage.styles.main}>
              <h3 className={AccountFavouritesPage.styles.heading}>{i18n.titleTracks}</h3>
              {!model.hasTracks && (
                <div className={AccountFavouritesPage.styles.empty}>
                  {model.noTracks}
                </div>
              )}
              {model.hasTracks && (
                <div>
                  <ReleaseTracks
                    model={model.infiniteTracks}
                    AddRemove={AddRemoveCompat}
                    PlayButton={PlayButtonCompat}
                    Favourite={FavouriteButtonCompat}
                    BrandSponsoredLabel={BrandSponsoredLabelCompat}
                    isMobile={isMobile}
                    onClickLink={handleClickLink}
                    preloadWaveform={preloadWaveformSvg}
                  />
                  {model.hasMoreTracks && !model.loadingMoreTracks && (
                    <div className={AccountFavouritesPage.styles.loadMore}>
                      <ButtonPill onClick={controller.loadMoreTracks}>Load more</ButtonPill>
                    </div>
                  )}
                  {model.loadingMoreTracks && (
                    <div className={AccountFavouritesPage.styles.loadMore}>
                      <Loading />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={AccountFavouritesPage.styles.aside}>
              <h3 className={AccountFavouritesPage.styles.heading}>{i18n.titleArtists}</h3>
              {!model.hasArtists && (
                <div className={AccountFavouritesPage.styles.empty}>
                  {model.noArtists}
                </div>
              )}
              {model.hasArtists && (
                <div>
                  <div className={AccountFavouritesPage.styles.grid}>
                    {model.filteredArtists.map((artist, index) => (
                      <ArtistTile
                        key={index.toString()}
                        artist={artist}
                        preload={preloadImage}
                        Favourite={FavouriteArtistButtonCompat}
                        onClickLink={handleClickLink}
                      />
                    ))}
                  </div>
                  {model.hasMoreArtists && !model.loadingMoreArtists && (
                    <div className={AccountFavouritesPage.styles.loadMore}>
                      <ButtonPill onClick={controller.loadMoreArtists}>Load more</ButtonPill>
                    </div>
                  )}
                  {model.loadingMoreArtists && (
                    <div className={AccountFavouritesPage.styles.loadMore}>
                      <Loading />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </AccountLayout>
    )
  }

  static i18n = {
    loading: 'Loading...',
    title: 'Favourites',
    titleTracks: 'Favourite tracks',
    titleArtists: 'Favourite artists'
  };

  static styles = {
    container: style({

    }),
    loading: style({
      height: px(100),
      ...centerCenter
    }),
    body: style({
      padding: px(20),
      background: colorSlate.toString(),
      color: colorWhite.toString()
    }),
    empty: style({
      ...centerCenter,
      height: px(120),
      fontWeight: 200,
      fontSize: px(18),
      color: colorSubtle.toString()
    }),
    split: style(
      {
        ...horizontal,
        margin: 0,
      },
      mediaMobileOnly({
        flexDirection: 'column-reverse',
        padding: '0 20px'
      })
    ),
    main: style(
      {
        overflow: 'hidden',
        position: 'relative',
        flex: 2,
        margin: '0 20px 0 0',
        height: percent(100)
      },
      mediaMobileOnly({
        overflow: 'auto',
        width: percent(100),
        margin: 0,
        height: 'auto',
        flex: 'none'
      })
    ),
    aside: style(
      {
        position: 'relative',
        flex: 1,
        margin: '0 20px 0 0',
        height: percent(100)
      },
      mediaMobileOnly({
        width: percent(100),
        margin: 0,
        height: 'auto',
        flex: 'none'
      })
    ),
    heading: style({
      margin: '30px 0'
    }),
    loadMore: style({
      ...centerCenter,
      padding: '20px'
    }),
    grid: style({
      ...horizontal,
      ...wrap,
      width: percent(100),
      $nest: {
        '&> *': {
          width: percent(50)
        }
      }
    })
  };
}